<template>
	<div class="pc-footer" :class="{ 'pc-footer--en': isEn }" :lang="isEn ? 'en' : 'zh-TW'">
		<div class="pc-footer-content">
			<div class="top-wrap">
				<div class="company-information">
					<div class="title">{{ footerCopy.companyTitle }}</div>
					<ul>
						<li
							@click="handleFooterLink(item)"
							v-for="(item, index) in companyInformation"
							:key="index"
						>
							{{ item.text }}
						</li>
					</ul>
				</div>
				<div class="helps">
					<div class="title">{{ footerCopy.helpTitle }}</div>
					<ul>
						<li @click="handleFooterLink(item)" v-for="(item, index) in helps" :key="index">
							{{ item.text }}
						</li>
					</ul>
				</div>
				<div class="info">
					<img class="logo" :src="footerLogoSrc" alt="logo" />
					<div class="info-item">
						<div class="label">{{ footerCopy.serviceHoursLabel }}</div>
						<div class="value">{{ contactInfo.serviceHours }}</div>
					</div>
					<div class="info-item">
						<div class="label">{{ footerCopy.servicePauseLabel }}</div>
						<div class="value">{{ contactInfo.servicePause }}</div>
					</div>
					<div class="info-item">
						<div class="label">{{ footerCopy.companyNameLabel }}</div>
						<div class="value">{{ contactInfo.companyName }}</div>
					</div>
					<div class="info-item">
						<div class="label">{{ footerCopy.addressLabel }}</div>
						<div class="value">{{ contactInfo.address }}</div>
					</div>
					<div class="info-item">
						<div class="label">{{ footerCopy.contactEmailLabel }}</div>
						<div class="value">{{ contactInfo.contactEmail }}</div>
					</div>
					<div class="info-item">
						<div class="label">{{ footerCopy.businessEmailLabel }}</div>
						<div class="value">{{ contactInfo.businessEmail }}</div>
					</div>
				</div>
			</div>

			<div class="center-wrap">
				<div class="we-accept">
					<div class="title">{{ footerCopy.weAcceptTitle }}</div>
					<ul>
						<img class="pay-icon" v-for="(v, i) in payIconList" :key="i" :src="v" alt="" />
					</ul>
				</div>

				<div class="follows">
					<div class="title">{{ footerCopy.followTitle }}</div>
					<ul class="follow-list">
						<li v-for="(item, index) in contactList" :key="index" @click="toMedia(item)">
							<img :src="getContactIcon(item.icon)" />
						</li>
					</ul>
					<!-- <ClientOnly>
						<div class="subscribe" v-if="!isLogin">
							<n-auto-complete
								v-model:value="email"
								:input-props="{
									autocomplete: 'disabled',
								}"
								:options="options"
								placeholder="請輸入郵箱"
								clearable
							/>
							<div :class="email.length > 0 ? 'btn' : 'btn no-submit'" @click="toLogin">訂閱</div>
						</div>
					</ClientOnly> -->
				</div>
			</div>

			<div class="bottom-wrap">
				<p class="copyright">{{ footerCopy.copyright }}</p>
				<ul class="xieyi-list">
					<li v-for="(item, index) in xieyiList" :key="index" @click="handleFooterLink(item)">
						<span>{{ item.name }}</span>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script setup>
import { goLink } from "~/utils/common";
import { emailSuffixes } from "~/utils/constants";
import { ref, inject, computed } from "vue";
import { sendFollowEmail } from "~/api/api";
import EmailValidator from "email-validator";
import showMessage from "~/utils/showMessage";
import { KEFU_LINK } from "~/utils/constants";
import {
	getEmailDomain,
	resolveSocialPlatform,
	useSiteComponentTracking,
} from "~/composables/useSiteComponentTracking";
import { stripLocalePrefix } from "~/utils/i18n-locale-routes";
// 导入所有支付图标
import visaIcon from "./images/pay-icon-list/visa.png";
import paypalIcon from "./images/pay-icon-list/paypal.png";
import octopusIcon from "./images/pay-icon-list/octopus.png";
import paymeIcon from "./images/pay-icon-list/payme.png";
import alipayIcon from "./images/pay-icon-list/alipay.png";
import wechatpayIcon from "./images/pay-icon-list/WeChatpay.png";
import linepayIcon from "./images/pay-icon-list/linepay.png";
import pxpayplusIcon from "./images/pay-icon-list/pxpayplus.png";
import icashIcon from "./images/pay-icon-list/icash.png";
import myacrdIcon from "./images/pay-icon-list/myacrd.png";
import touchIcon from "./images/pay-icon-list/touch.png";
import fpxIcon from "./images/pay-icon-list/FPX.png";
import shopeeIcon from "./images/pay-icon-list/shopee.png";
import grabIcon from "./images/pay-icon-list/grab.png";
import danaIcon from "./images/pay-icon-list/dana.png";

// 导入其他图片
import facebookIcon from "./images/facebook@2x (1).png";
import insIcon from "./images/ins@2x.png";
import lineIcon from "./images/Line@2x (1).png";
import whatsappIcon from "./images/WhatsApp@2x.png";
import youtubeIcon from "./images/YouTube@2x.png";
import tiktokIcon from "./images/tiktok@2x.png";
import footerLogoZh from "~/assets/images/SeerQtw.svg";
import footerLogoEn from "~/assets/images/SeerQ.svg";

const props = defineProps({
	/** 傳入 `en` 時展示英文文案，其餘（如 zh-TW）為繁中 */
	lang: {
		type: String,
		default: "zh-TW",
	},
	isLogin: {
		type: Boolean,
		default: false,
	},
});

const isEn = computed(() => props.lang === "en");
const { track } = useSiteComponentTracking("pc");
const route = useRoute();

const footerLogoSrc = computed(() => (isEn.value ? footerLogoEn : footerLogoZh));

const contactInfo = computed(() => ({
	serviceHours: isEn.value ? "09:00–02:00 next day (UTC+8)" : "09:00–次日02:00（UTC+8）",
	servicePause: isEn.value ? "13:00–14:00, 20:00–21:00" : "13:00–14:00、20:00–21:00",
	companyName: "FAITH ONNET LIMITED",
	address: "Room 2003, 20th Floor, COFCO TOWER, No.262 Gloucester Road, Causeway Bay, Hong Kong",
	contactEmail: "service@seeronnet.com",
	businessEmail: "tarry@seeronnet.com",
}));

const footerCopy = computed(() =>
	isEn.value
		? {
				companyTitle: "Company",
				helpTitle: "Help",
				serviceHoursLabel: "Customer Service Hours:",
				servicePauseLabel: "Service Suspended:",
				companyNameLabel: "Company Name:",
				addressLabel: "Address:",
				contactEmailLabel: "Contact Email:",
				businessEmailLabel: "Business Cooperation:",
				weAcceptTitle: "We Accept",
				followTitle: "Follow SeerQ",
				copyright: "Copyright © 2026 Seer. All Rights Reserved.",
			}
		: {
				companyTitle: "公司資訊",
				helpTitle: "幫助",
				serviceHoursLabel: "客服服務時間：",
				servicePauseLabel: "服務暫停：",
				companyNameLabel: "公司名稱：",
				addressLabel: "聯絡地址：",
				contactEmailLabel: "聯絡電郵：",
				businessEmailLabel: "商務合作電郵：",
				weAcceptTitle: "我們接受",
				followTitle: "追蹤先知命局",
				copyright: "Copyright © 2026先知命局 版權所有",
			}
);

const email = ref("");
const openEmailOfficail = inject("openEmailOfficail", () => {});

const toLogin = () => {
	let urlObj = typeof window !== "undefined" ? new URL(window.location.href) : null;
	if (!urlObj) return;

	if (email.value?.length == 0) {
		track("footer_subscribe_submit", {
			result: "fail",
		});
		showMessage(isEn.value ? "Email is required" : "邮箱不能为空");
		return;
	}

	const emailDomain = getEmailDomain(email.value);
	if (EmailValidator.validate(email.value)) {
		sendFollowEmail({
			email: email.value,
			return_url: `${urlObj.protocol}//${urlObj.host}/`,
		})
			.then(() => {
				track("footer_subscribe_submit", {
					email_domain: emailDomain,
					result: "success",
				});
				openEmailOfficail?.();
			})
			.catch(() => {
				track("footer_subscribe_submit", {
					email_domain: emailDomain,
					result: "fail",
				});
			});
	} else {
		track("footer_subscribe_submit", {
			email_domain: emailDomain,
			result: "fail",
		});
		showMessage(isEn.value ? "Invalid email format" : "郵箱格式錯誤");
	}
};

// 根据域名修改主体跟Slogan
// const hspDomain = "www.hspiritlink.com";
// const isHspDomain = ref(false); //是否是 www.hspiritlink.com 域名
// onMounted(() => {
// 	if (import.meta.client) {
// 		if (location.hostname === hspDomain) {
// 			isHspDomain.value = true;
// 		}
// 	}
// });

// 邮箱后缀
const options = computed(() => {
	return emailSuffixes.map(suffix => {
		const inputValue = email.value?.split("@")[0];
		return {
			label: inputValue + suffix,
			value: inputValue + suffix,
		};
	});
});

// 联系方式
const contactList = [
	{
		icon: "YouTube.png",
		url: "https://www.youtube.com/@SeerQ-001",
	},
	{
		icon: "WhatsApp.png",
		url: "https://wa.me/85259224753",
	},
	{
		icon: "facebook.png",
		url: "https://www.facebook.com/profile.php?id=61578513161082",
	},
	{
		icon: "Line.png",
		url: "https://lin.ee/P06Koie",
	},
	{
		icon: "ins.png",
		url: "https://www.instagram.com/official.seeronnet/",
	},
	{
		icon: "tiktok.png",
		url: "https://www.tiktok.com/@official.seer",
	},
];

function scrollToHashTarget(hash) {
	if (!import.meta.client || !hash) return false;
	const id = hash.replace(/^#/, "");
	const el = document.getElementById(id);
	if (!el) return false;

	const header = document.querySelector(".landing-header");
	const offset = header instanceof HTMLElement ? header.offsetHeight + 12 : 96;
	const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
	window.scrollTo({ top, behavior: "smooth" });
	return true;
}

function handleFooterLink(item) {
	track("footer_link_click", {
		element_id: item.elementId,
		element_text: item.text || item.name,
		target_url: item.url,
	});
	// /dist/* 是独立静态子应用，不能走 Nuxt 客户端路由，否则会命中站内 404
	if (item.url?.startsWith("/dist/")) {
		goLink(item.url);
		return;
	}
	if (item.url?.startsWith("/")) {
		navigateTo(item.url);
		return;
	}
	goLink(item.url);
}

const toMedia = item => {
	track("footer_social_click", {
		social_platform: resolveSocialPlatform(item.icon),
		target_url: item.url,
	});
	goLink(item.url);
};

// protocol 项目 getBrowserLang 只认 en_us / zh_tw / zh_cn（不会把 en 别名映射进去）
const protocolLang = computed(() => (isEn.value ? "en_us" : "zh_tw"));

const xieyiList = computed(() => {
	const q = `lang=${protocolLang.value}`;
	if (isEn.value) {
		return [
			{
				text: "Privacy Policy Statement",
				url: `/dist/protocol/website/privacy-policy-statement?${q}`,
				name: "Privacy Policy Statement",
				elementId: "footer_privacy_policy",
			},
			{
				text: "Personal Information Collection Statement",
				url: `/dist/protocol/website/personal-info-collection-statement?${q}`,
				name: "Personal Information Collection Statement",
				elementId: "footer_personal_info",
			},
			{
				text: "Platform Terms of Use",
				url: `/dist/protocol/website/platform-terms-of-use?${q}`,
				name: "Platform Terms of Use",
				elementId: "footer_platform_terms",
			},
			{
				text: "Copyright Notice and Takedown Procedure",
				url: `/dist/protocol/website/copyright-notice-takedown?${q}`,
				name: "Copyright Notice and Takedown Procedure",
				elementId: "footer_copyright_notice",
			},
			{
				text: "Service Agreement",
				url: `/dist/protocol/website/service-agreement?${q}`,
				name: "Service Agreement",
				elementId: "footer_service_agreement",
			},
			{
				text: "Refund Policy",
				url: `/dist/protocol/website/refund-policy?${q}`,
				name: "Refund Policy",
				elementId: "footer_refund_policy",
			},
		];
	}
	return [
		{
			text: "私隱政策聲明",
			url: `/dist/protocol/website/privacy-policy-statement?${q}`,
			name: "私隱政策聲明",
			elementId: "footer_privacy_policy",
		},
		{
			text: "個人資料收集聲明",
			url: `/dist/protocol/website/personal-info-collection-statement?${q}`,
			name: "個人資料收集聲明",
			elementId: "footer_personal_info",
		},
		{
			text: "平台使用條款",
			url: `/dist/protocol/website/platform-terms-of-use?${q}`,
			name: "平台使用條款",
			elementId: "footer_platform_terms",
		},
		{
			text: "著作權通知和移除程序",
			url: `/dist/protocol/website/copyright-notice-takedown?${q}`,
			name: "著作權通知和移除程序",
			elementId: "footer_copyright_notice",
		},
		{
			text: "服務協議",
			url: `/dist/protocol/website/service-agreement?${q}`,
			name: "服務協議",
			elementId: "footer_service_agreement",
		},
		{
			text: "退款政策",
			url: `/dist/protocol/website/refund-policy?${q}`,
			name: "退款政策",
			elementId: "footer_refund_policy",
		},
	];
});

const companyInformation = computed(() =>
	isEn.value
		? [
				{
					text: "About SeerQ",
					url: "/seer/aboutus",
					elementId: "footer_about",
				},
				{
					text: "Newsroom",
					url: "/seer/aboutus?to=newsroom",
					elementId: "footer_newsroom",
				},
			]
		: [
				{
					text: "關於先知命局",
					url: "/seer/aboutus",
					elementId: "footer_about",
				},
				{
					text: "新聞室",
					url: "/seer/aboutus?to=newsroom",
					elementId: "footer_newsroom",
				},
			]
);

const helps = computed(() =>
	isEn.value
		? [
				{
					text: "Online Customer Support",
					url: KEFU_LINK,
					elementId: "footer_help_center",
				},
				{
					text: "Partner with SeerQ",
					url: "/seer/aboutus?to=cooperate",
					elementId: "footer_cooperate",
				},
			]
		: [
				{
					text: "線上客戶支援",
					url: KEFU_LINK,
					elementId: "footer_help_center",
				},
				{
					text: "與先知命局合作",
					url: "/seer/aboutus?to=cooperate",
					elementId: "footer_cooperate",
				},
			]
);

const payIconMap = {
	visa: visaIcon,
	paypal: paypalIcon,
	octopus: octopusIcon,
	payme: paymeIcon,
	alipay: alipayIcon,
	WeChatpay: wechatpayIcon,
	linepay: linepayIcon,
	pxpayplus: pxpayplusIcon,
	icash: icashIcon,
	myacrd: myacrdIcon,
	touch: touchIcon,
	FPX: fpxIcon,
	shopee: shopeeIcon,
	grab: grabIcon,
	dana: danaIcon,
};

const contactIconMap = {
	"YouTube.png": youtubeIcon,
	"WhatsApp.png": whatsappIcon,
	"facebook.png": facebookIcon,
	"Line.png": lineIcon,
	"ins.png": insIcon,
	"tiktok.png": tiktokIcon,
};

const getContactIcon = iconName => {
	return contactIconMap[iconName] || "";
};

const ALL_PAY_ICONS = [
	"visa",
	"paypal",
	"octopus",
	"payme",
	"alipay",
	"WeChatpay",
	"linepay",
	"pxpayplus",
	"icash",
	"myacrd",
	"touch",
	"FPX",
	"shopee",
	"grab",
	"dana",
];

const payIconList = computed(() => ALL_PAY_ICONS.map(v => payIconMap[v]));
</script>

<script>
export default {
	name: "PcFooter",
};
</script>

<style lang="scss" scoped>
.pc-footer {
	width: 100%;
	background: #fff;
	border-top: 1px solid #efefef;

	.subscribe {
		width: 300px;
		display: flex;
		padding-top: 23px;

		.btn {
			width: 87px;
			height: 32px;
			background: #f2af19;
			border-radius: 0 4px 4px 0;
			text-align: center;
			line-height: 32px;
			font-size: 18px;
			font-family: Source Han Sans CN;
			color: #fff;
			letter-spacing: 3px;
			cursor: pointer;
		}

		.no-submit {
			opacity: 0.5;
			cursor: not-allowed;
		}

		:deep(.n-input) {
			width: 234px !important;
			height: 32px !important;
			line-height: 32px !important;
			border-radius: 4px 0 0 4px;
			border: 1px solid #efefef !important;
			border-right: none !important;
			--n-border: 1px solid #efefef !important;
			--n-border-focus: 1px solid #f2af19 !important;
			--n-border-hover: 1px solid #f2af19 !important;
			--n-box-shadow-focus: 0 0 0 0 rgba(242, 175, 25, 0.2) !important;
			--n-color: #fff !important;
			--n-color-focus: #fff !important;
			--n-color-disabled: #fff !important;
			--n-placeholder-color: #c9843d !important;
		}
	}

	.pc-footer-content {
		padding-top: 36px;
		margin: 0 auto;
		width: min(1200px, calc(100% - 48px));
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		overflow: hidden;

		.top-wrap {
			display: flex;
			gap: 48px;

			.company-information,
			.helps {
				.title {
					margin-top: 22px;
					margin-bottom: 25px;
					font-weight: bold;
					font-size: 18px;
					color: #333;
				}

				ul {
					li {
						white-space: nowrap;
						font-size: 15px;
						color: #a0a0a0;
						line-height: 32px;
						cursor: pointer;
					}
				}
			}

			// .company-information {
			// 	li {
			// 		&:nth-child(2) {
			// 			font-size: 13px;
			// 		}
			// 	}
			// }

			.helps {
				margin: 0 auto 0 0;
			}

			.info {
				width: min(420px, 100%);
				font-size: 15px;
				color: #a0a0a0;
				line-height: 24px;

				.logo {
					margin-bottom: 25px;
					display: block;
					width: 200px;
				}

				.info-item {
					display: flex;
					gap: 4px;

					.label {
						white-space: nowrap;
						color: #333;
					}

					.value {
						min-width: 0;
						color: #a0a0a0;
						overflow-wrap: anywhere;
					}
				}
			}
		}

		.center-wrap {
			margin: 35px 0 20px;
			display: flex;
			align-items: flex-end;
			gap: 200px;

			.we-accept,
			.follows {
				.title {
					margin-bottom: 24px;
					font-family: Source Han Sans CN;
					font-weight: bold;
					font-size: 18px;
					color: #333;
				}

				ul {
					display: grid;
					grid-template-columns: repeat(auto-fit, minmax(64px, 72px));
					gap: 12px 8px;
				}

				.pay-icon {
					margin: 0;
					width: 72px;
				}

				.follow-list {
					display: flex;
					grid-template-columns: none;
					gap: 0;

					img {
						margin-right: 16px;
						width: 32px;
						height: 32px;
						cursor: pointer;
					}
				}
			}

			.we-accept {
				flex: 1;
			}

			.follows {
				width: min(417px, 100%);
			}
		}

		.bottom-wrap {
			padding: 37px 0;
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 24px;
			border-top: 1px solid #efefef;

			.xieyi-list {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-wrap: wrap;
				gap: 10px 40px;
				font-family: Source Han Sans CN;
				font-size: 13px;
				color: #a0a0a0;

				li {
					position: relative;

					span {
						cursor: pointer;
					}

					::after {
						content: "";
						width: 0;
						height: 12px;
						border-left: 1px solid #efefef;
						position: absolute;
						top: 50%;
						left: -21px;
						transform: translateY(-50%);
					}
				}
			}

			.copyright {
				font-family: Source Han Sans CN;
				font-size: 13px;
				color: #a0a0a0;
			}
		}
	}
}

@media (max-width: 900px) {
	.pc-footer {
		.pc-footer-content {
			width: min(100% - 32px, 702px);
			padding-top: 48px;
			padding-bottom: 0;
		}

		.top-wrap {
			flex-direction: column-reverse;
			gap: 42px;

			.company-information,
			.helps {
				.title {
					margin: 0 0 18px;
					font-size: 20px;
				}

				ul li {
					white-space: normal;
					font-size: 16px;
					line-height: 28px;
				}
			}

			.info {
				.logo {
					width: 180px;
				}

				.info-item {
					font-size: 16px;
					line-height: 28px;
				}
			}
		}

		.center-wrap {
			flex-direction: column-reverse;
			margin: 42px 0 0;
			gap: 42px;

			.we-accept,
			.follows {
				.title {
					margin-bottom: 20px;
					font-size: 20px;
				}
			}
		}

		.bottom-wrap {
			flex-direction: column-reverse;
			align-items: center;
			margin-top: 48px;
			padding: 32px 0;

			.xieyi-list {
				gap: 8px 28px;
				font-size: 14px;

				li::after {
					left: -15px;
				}
			}
		}
	}
}

@media (max-width: 640px) {
	.pc-footer {
		.pc-footer-content {
			width: calc(100% - 32px);
		}

		.top-wrap {
			.info {
				.info-item {
					align-items: flex-start;
					font-size: 14px;
					line-height: 24px;
				}
			}
		}

		.center-wrap {
			.we-accept {
				ul {
					grid-template-columns: repeat(auto-fit, minmax(86px, 1fr));
				}

				.pay-icon {
					width: 100%;
					max-width: 120px;
				}
			}

			.follow-list {
				flex-wrap: wrap;
				gap: 14px;

				img {
					margin-right: 0;
				}
			}
		}
	}
}
</style>
